// Estimated ages at registration (plan §12, decision 12).
//
// Many adults, and some children, arrive without a known date of birth. Reception enters
// an estimated age — whole years, or whole months for a child under two — and Wodi stores
// an estimated date of birth with `birth_date_estimated = true`, so the age keeps
// advancing correctly and every age-based rule reads one field. Everywhere the age is
// shown, it says it is estimated: "34 y (estimated)", "8 mo (estimated)".
//
// The estimated date is the same calendar day the given number of years or months before
// the day of registration (29 February and month ends clamp to the last day of the
// month). That makes the round trip exact: an age of 34 years entered today reads back as
// 34 years today.
//
// TODO(verify-spec): how the Client Registry accepts an estimated date of birth is not
// documented yet. Its mapping is decided in slice 1b; this module does not guess it.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';
import type { Age, CalendarDay } from './time.ts';
import { ageOnDay, daysInMonth, isValidDay, MONTHS_UNTIL_YEARS } from './time.ts';

export type EstimatedAge =
  | { readonly unit: 'years'; readonly value: number }
  | { readonly unit: 'months'; readonly value: number };

/**
 * The oldest estimated age reception can enter. A data-entry bound, set above the oldest
 * documented human age so it never refuses a real person; it is not a clinical rule.
 */
export const MAX_ESTIMATED_AGE_YEARS = 130;

/** Years are entered from 2; under two, the age is entered in months (0–23). */
export const MIN_ESTIMATED_AGE_YEARS = MONTHS_UNTIL_YEARS / 12;

export type EstimatedAgeErrorCode =
  | 'estimated_age.not_whole_number'
  | 'estimated_age.use_months'
  | 'estimated_age.use_years'
  | 'estimated_age.too_old';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const ESTIMATED_AGE_ERRORS: Record<EstimatedAgeErrorCode, string> = {
  'estimated_age.not_whole_number': 'An estimated age is a whole number',
  'estimated_age.use_months': 'For a child under 2, enter the age in months',
  'estimated_age.use_years': 'From 2 years old, enter the age in years',
  'estimated_age.too_old': `An estimated age is at most ${String(MAX_ESTIMATED_AGE_YEARS)} years`,
};

function monthsBefore(day: CalendarDay, months: number): CalendarDay {
  const index = day.year * 12 + (day.month - 1) - months;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  return { year, month, day: Math.min(day.day, daysInMonth(year, month)) };
}

/** Checks an estimated age as entered, before it becomes a date. */
export function validateEstimatedAge(
  age: EstimatedAge,
): Result<EstimatedAge, EstimatedAgeErrorCode> {
  if (!Number.isInteger(age.value) || age.value < 0) return err('estimated_age.not_whole_number');
  if (age.unit === 'years') {
    if (age.value < MIN_ESTIMATED_AGE_YEARS) return err('estimated_age.use_months');
    if (age.value > MAX_ESTIMATED_AGE_YEARS) return err('estimated_age.too_old');
  } else if (age.value >= MONTHS_UNTIL_YEARS) {
    return err('estimated_age.use_years');
  }
  return ok(age);
}

/** The estimated date of birth for an age entered on `today` (an EAT calendar day). */
export function estimatedBirthDate(
  age: EstimatedAge,
  today: CalendarDay,
): Result<CalendarDay, EstimatedAgeErrorCode> {
  if (!isValidDay(today)) throw new RangeError('Not a date on the calendar');
  const checked = validateEstimatedAge(age);
  if (!checked.ok) return checked;
  const months = age.unit === 'years' ? age.value * 12 : age.value;
  return ok(monthsBefore(today, months));
}

/**
 * The estimated age an estimated birth date represents on `today`, in the unit reception
 * would enter it: months under two, years from two. Used to pre-fill a correction form.
 */
export function estimatedAgeOn(birthDate: CalendarDay, today: CalendarDay): EstimatedAge {
  const age = ageOnDay(birthDate, today);
  if (age.unit === 'years') return { unit: 'years', value: age.value };
  if (age.unit === 'months') return { unit: 'months', value: age.value };
  return { unit: 'months', value: 0 };
}

/** `34 y`, `8 mo`, `6 d`: the short form the banner uses ("F · 34 y", DESIGN.md §6). */
export function formatAgeShort(age: Age): string {
  const unit = age.unit === 'years' ? 'y' : age.unit === 'months' ? 'mo' : 'd';
  return `${String(age.value)} ${unit}`;
}

/**
 * A patient's age for the banner and search results, on `today`. An estimated age is
 * marked, and never shown in days: a count of days implies a precision an estimate
 * doesn't have, so an estimated newborn reads "0 mo (estimated)".
 */
export function formatPatientAge(
  birthDate: CalendarDay,
  estimated: boolean,
  today: CalendarDay,
): string {
  if (!estimated) return formatAgeShort(ageOnDay(birthDate, today));
  const age = estimatedAgeOn(birthDate, today);
  return `${formatAgeShort(age)} (estimated)`;
}
